import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth'
import { auth, firebaseEnabled, googleProvider } from '../../ultron-turing-manager/src/services/firebase'
import { checkAdminStatus } from '../../ultron-turing-manager/src/services/adminApi'
import {
  acceptAppSuiteUserInvitation,
  getAppSuiteCurrentClientId,
  getAppSuiteUserAccess,
  setAppSuiteActiveClientId,
  syncAppSuiteClientIdFromRuntime,
} from '../services/appsuiteApi'
import './AppSuiteLoginPage.css'

const DEFAULT_TEMPLATE_PATH = '/appsuite/templates'
const POST_AUTH_REDIRECT_KEY = 'utm:postAuthPath'
const ACCESS_TOKEN_EXPIRED_HINT = 'Access token expired'
const TOKEN_EXPIRED_HINT_ZH = '已過期'
const CANONICAL_STAGING_HOST = 'ultron-turing-project.web.app'

function normalizeRedirectPath(rawInput: string): string {
  const raw = String(rawInput || '').trim()
  if (!raw.startsWith('/') || raw.startsWith('//')) return DEFAULT_TEMPLATE_PATH
  const normalized = raw.replace(/^\/appfactory/, '/appsuite')
  const [pathWithQuery, hash = ''] = normalized.split('#')
  const [pathname, query = ''] = pathWithQuery.split('?')
  const params = new URLSearchParams(query)
  params.delete('invite')
  const nextQuery = params.toString()
  const rebuilt = `${pathname}${nextQuery ? `?${nextQuery}` : ''}${hash ? `#${hash}` : ''}`
  return rebuilt || DEFAULT_TEMPLATE_PATH
}

function readStoredPostAuthPath(): string {
  if (typeof window === 'undefined') return ''
  try {
    const raw = String(window.sessionStorage.getItem(POST_AUTH_REDIRECT_KEY) || '').trim()
    if (!raw.startsWith('/') || raw.startsWith('//')) return ''
    return raw
  } catch {
    return ''
  }
}

function takeStoredPostAuthPath(): string {
  if (typeof window === 'undefined') return ''
  try {
    const raw = String(window.sessionStorage.getItem(POST_AUTH_REDIRECT_KEY) || '').trim()
    window.sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY)
    if (!raw.startsWith('/') || raw.startsWith('//')) return ''
    return raw
  } catch {
    return ''
  }
}

function readRedirectPath(storedPath = ''): string {
  const raw = new URLSearchParams(window.location.search).get('redirect') || storedPath || readStoredPostAuthPath()
  return normalizeRedirectPath(raw)
}

function readInviteToken(storedPath = ''): string {
  const topLevel = new URLSearchParams(window.location.search).get('invite')
  if (topLevel) return topLevel
  const redirectRaw = new URLSearchParams(window.location.search).get('redirect') || storedPath || readStoredPostAuthPath()
  if (!redirectRaw.startsWith('/')) return ''
  const query = redirectRaw.split('?')[1] || ''
  return new URLSearchParams(query).get('invite') || ''
}

function isTokenExpiredMessage(input: unknown): boolean {
  const message = String(input || '')
  return (
    message.includes(ACCESS_TOKEN_EXPIRED_HINT) ||
    message.includes(TOKEN_EXPIRED_HINT_ZH) ||
    message.includes('認證失敗 (401)') ||
    message.includes('Unauthorized')
  )
}

export default function AppSuiteLoginPage() {
  const navigate = useNavigate()
  const resolvingAccessRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [checking, setChecking] = useState(true)
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : ''
  const isLegacyStagingPreviewHost = hostname.includes('--staging-') && hostname.endsWith('.web.app')

  useEffect(() => {
    syncAppSuiteClientIdFromRuntime()
  }, [])

  useEffect(() => {
    // Keep auth host behavior aligned with UTM login page.
    if (!isLegacyStagingPreviewHost) return
    const targetUrl = `https://${CANONICAL_STAGING_HOST}${window.location.pathname}${window.location.search}${window.location.hash}`
    window.location.replace(targetUrl)
  }, [isLegacyStagingPreviewHost])

  useEffect(() => {
    if (!auth) {
      setChecking(false)
      setError('Firebase Auth is not configured.')
      return
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user?.email) {
        resolvingAccessRef.current = false
        setChecking(false)
        return
      }
      if (resolvingAccessRef.current) return
      resolvingAccessRef.current = true
      try {
        const postAuthPath = takeStoredPostAuthPath()
        const tokenForBootstrap = await user.getIdToken(true)
        const adminBootstrap = await checkAdminStatus(user.email, tokenForBootstrap).catch(() => ({
          isAdmin: false,
          status: null,
          role: null,
        }))

        // Root/admin accounts can enter without probing /appsuite/users/me/access first.
        // This avoids noisy 401 logs on environments where that endpoint auth path isn't synced yet.
        if (adminBootstrap.isAdmin) {
          const fallbackClientId = getAppSuiteCurrentClientId()
          if (fallbackClientId) setAppSuiteActiveClientId(fallbackClientId)
          navigate(readRedirectPath(postAuthPath), { replace: true })
          return
        }

        const ensureAccess = async () => {
          try {
            await user.reload()
          } catch {
            // ignore and continue with token refresh
          }
          await user.getIdToken(true)
          return getAppSuiteUserAccess()
        }

        const ensureAccessWithFallback = async () => {
          try {
            return await ensureAccess()
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error || '')
            const maybeAuth401 = isTokenExpiredMessage(message)
            if (!maybeAuth401 || !user.email) throw error

            // Compatibility fallback for environments where /appsuite/users auth middleware is not yet synced.
            // Root admin can still proceed; App Suite pages will enforce per-client permissions.
            const idToken = await user.getIdToken(true)
            const admin = await checkAdminStatus(user.email, idToken)
            if (!admin.isAdmin) throw error
            const fallbackClientId = getAppSuiteCurrentClientId()
            return {
              hasAccess: true,
              defaultClientId: fallbackClientId || null,
              clients: [],
              user: {
                email: user.email,
                userId: user.uid,
              },
            }
          }
        }

        let access
        const inviteToken = readInviteToken(postAuthPath).trim()
        if (inviteToken) {
          await user.getIdToken(true)
          const acceptResult = await acceptAppSuiteUserInvitation(inviteToken)
          if (!acceptResult.success) {
            throw new Error(acceptResult.error || 'Invitation acceptance failed')
          }
        }

        try {
          access = await ensureAccessWithFallback()
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error || '')
          if (isTokenExpiredMessage(message)) {
            await new Promise((resolve) => setTimeout(resolve, 900))
            access = await ensureAccessWithFallback()
          } else {
            throw error
          }
        }

        const runtimeClientId = getAppSuiteCurrentClientId()
        const defaultClientId = String(runtimeClientId || access.defaultClientId || '').trim()
        if (defaultClientId) setAppSuiteActiveClientId(defaultClientId)
        navigate(readRedirectPath(postAuthPath), { replace: true })
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to verify App Suite access'
        if (isTokenExpiredMessage(message)) {
          // Self-heal once before forcing user to re-login.
          try {
            if (auth?.currentUser) {
              await auth.currentUser.reload()
              await auth.currentUser.getIdToken(true)
              const recoveredAccess = await getAppSuiteUserAccess()
              const recoveredClientId = String(recoveredAccess.defaultClientId || '').trim()
              if (recoveredClientId) setAppSuiteActiveClientId(recoveredClientId)
              navigate(readRedirectPath(), { replace: true })
              return
            }
          } catch {
            // fall through to user-facing error
          }
          try {
            if (auth?.currentUser) {
              await signOut(auth)
            }
          } catch {
            // ignore
          }
          setError('登入憑證已過期，系統已嘗試自動刷新但失敗。請按「Sign in with ULTRON」重新登入。')
        } else {
          setError(message)
        }
        setChecking(false)
        resolvingAccessRef.current = false
      }
    })

    return () => unsubscribe()
  }, [navigate])

  const handleSignIn = async () => {
    if (!auth || !googleProvider) {
      setError('Firebase Auth is not configured.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Sign-in failed'
      setError(message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="appsuite-login-page">
      <div className="appsuite-login-blob appsuite-login-blob-blue" />
      <div className="appsuite-login-blob appsuite-login-blob-primary" />
      <div className="appsuite-login-blob appsuite-login-blob-teal" />

      <div className="appsuite-login-shell">
        <section className="appsuite-login-brand-panel">
          <div className="appsuite-login-brand-copy">
            <div className="appsuite-login-brand-header">
              <div className="appsuite-login-brand-icon">
                <span className="material-symbols-outlined">home_iot_device</span>
              </div>
              <span className="appsuite-login-brand-text">Ultron AppSuite</span>
            </div>
            <h1 className="appsuite-login-title">
              From Concept
              <br />
              to App Store,
              <br />
              <span className="appsuite-login-title-gradient">Instantly.</span>
            </h1>
            <p className="appsuite-login-subtitle">
              Rapidly build and deploy native Smart Home apps with our intuitive visual builder.
            </p>
          </div>
          <div className="appsuite-login-hero-image" />
        </section>

        <section className="appsuite-login-auth-panel">
          <div className="appsuite-login-auth-body">
            <div className="appsuite-login-auth-header">
              <h2>Welcome back</h2>
              <p>Sign in to continue to your dashboard.</p>
            </div>

            <button
              type="button"
              className="appsuite-login-primary-btn"
              disabled={!firebaseEnabled || busy || checking}
              onClick={handleSignIn}
            >
              <span className="material-symbols-outlined">fingerprint</span>
              <span>{busy || checking ? 'Signing in...' : 'Sign in with ULTRON'}</span>
            </button>

            {!firebaseEnabled && (
              <div className="appsuite-login-error">Firebase is disabled. Check your env configuration.</div>
            )}
            {error && <div className="appsuite-login-error">{error}</div>}

            <div className="appsuite-login-divider">
              <span>or</span>
            </div>

            <div className="appsuite-login-form-mock">
              <input type="email" placeholder="Email address" disabled />
              <input type="password" placeholder="Password" disabled />
              <button
                type="button"
                className="appsuite-login-secondary-btn"
                disabled={!firebaseEnabled || busy || checking}
                onClick={handleSignIn}
              >
                Sign In
              </button>
            </div>

            <button type="button" className="appsuite-login-request-btn">
              Request Access
            </button>
          </div>

          <div className="appsuite-login-footer-note">Secure login powered by Ultron Identity</div>
        </section>
      </div>
    </div>
  )
}
